/**
 * Shared limits for bulk notice assignment (phone MSG91 cost control).
 * Keep in sync with kserver/src/utils/bulk-notice-limits.ts
 */

export const MAX_BULK_DP_COUNT = 100;
export const MAX_BULK_PHONE_BILLABLE_UNITS = 50;

export type BulkPhoneDeliveryMethod = 'SMS' | 'WHATSAPP' | 'BOTH';

export function phoneDeliveryMultiplier(method: BulkPhoneDeliveryMethod): number {
    return method === 'BOTH' ? 2 : 1;
}

export function computeBillableUnits(
    phoneRecipientCount: number,
    noticeCount: number,
    deliveryMethod: BulkPhoneDeliveryMethod
): number {
    if (phoneRecipientCount <= 0 || noticeCount <= 0) {
        return 0;
    }
    return phoneRecipientCount * noticeCount * phoneDeliveryMultiplier(deliveryMethod);
}

export function validateBulkDpCount(dpCount: number): string | null {
    if (dpCount <= 0) {
        return 'At least one data principal must be selected.';
    }
    if (dpCount > MAX_BULK_DP_COUNT) {
        return `Too many data principals selected (max ${MAX_BULK_DP_COUNT}).`;
    }
    return null;
}

export function validateBulkBillableUnits(
    phoneRecipientCount: number,
    noticeCount: number,
    deliveryMethod: BulkPhoneDeliveryMethod | undefined
): string | null {
    if (phoneRecipientCount <= 0 || !deliveryMethod) {
        return null;
    }
    const units = computeBillableUnits(phoneRecipientCount, noticeCount, deliveryMethod);
    if (units > MAX_BULK_PHONE_BILLABLE_UNITS) {
        const mult = phoneDeliveryMultiplier(deliveryMethod);
        return (
            `This send would use ${units} billable phone messages ` +
            `(${phoneRecipientCount} phone × ${noticeCount} notice(s) × ${mult} channel(s)), ` +
            `max ${MAX_BULK_PHONE_BILLABLE_UNITS}. ` +
            `Reduce selection, use SMS or WhatsApp only, or assign fewer notices.`
        );
    }
    return null;
}

export function formatBillableUnitsEstimate(
    phoneRecipientCount: number,
    noticeCount: number,
    deliveryMethod: BulkPhoneDeliveryMethod
): string {
    const mult = phoneDeliveryMultiplier(deliveryMethod);
    const units = computeBillableUnits(phoneRecipientCount, noticeCount, deliveryMethod);
    return `${phoneRecipientCount} phone × ${noticeCount} notice(s) × ${mult} = ${units} billable message(s) (max ${MAX_BULK_PHONE_BILLABLE_UNITS})`;
}
